/* eslint-disable react-refresh/only-export-components */
import React from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';
import * as Sentry from '@sentry/react';
import AuthLayout from './components/auth-layout';
import { getAuthToken, getAuthUser } from './lib/auth-store';
import {
  LoginScreen,
  SignupScreen,
  SignupDemoScreen,
  RegisterScreen,
  PasswordRecoveryScreen,
  PasswordRecoveryConfirmScreen,
  InvitationScreen,
  PaymentScreen,
  PaymentHistoryScreen,
  ConfigurationsScreen,
  TableDataDetailScreen,
  TableDataScreen,
  SearchScreen,
  SearchDetailScreen,
  ReportsScreen,
  CreateReportScreen,
  StrategicViewScreen,
  CompanyScreen,
  SummariesScreen,
  MyTeamScreen,
} from './screens';

function RouterErrorFallback({ error, reset }: { error: Error; reset: () => void }) {
  React.useEffect(() => {
    Sentry.captureException(error);
  }, [error]);

  return (
    <div className="flex min-h-[50vh] items-center justify-center bg-pale-grey">
      <div className="max-w-md rounded-xl bg-white p-6 text-center shadow-[0_4px_20px_-4px_rgba(26,46,88,0.25)]">
        <p className="text-lg font-semibold text-charcoal-grey">Ocurrió un error inesperado</p>
        <p className="mt-2 text-sm text-brownish-grey">
          Ya fue reportado automáticamente. Intenta recargar la página.
        </p>
        <div className="mt-4 flex justify-center gap-3">
          <button
            onClick={reset}
            className="rounded-lg border border-primary px-4 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          >
            Reintentar
          </button>
          <button
            onClick={() => window.location.reload()}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-primary/90"
          >
            Recargar página
          </button>
        </div>
      </div>
    </div>
  );
}

const RootLayout = () => (
  <div className="min-h-screen bg-pale-grey text-charcoal-grey">
    <Outlet />
  </div>
);

const rootRoute = createRootRoute({
  component: RootLayout,
});

const authRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
  beforeLoad: ({ location }) => {
    const token = getAuthToken();
    const user = getAuthUser();

    if (!token || !user) {
      return redirect({
        to: '/login',
        replace: true,
        search: { redirect: location.href },
      });
    }
  },
});

const publicRoutes = [
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/signup',
    component: SignupScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/signup/demo',
    component: SignupDemoScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/register',
    component: RegisterScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/password-recovery',
    component: PasswordRecoveryScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/recovery',
    component: PasswordRecoveryConfirmScreen,
  }),
  createRoute({
    getParentRoute: () => rootRoute,
    path: '/invitation',
    component: InvitationScreen,
  }),
];

const protectedRoutes = [
  createRoute({
    getParentRoute: () => authRoute,
    path: '/',
    component: TableDataDetailScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/payment',
    component: PaymentScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/payment-history',
    component: PaymentHistoryScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/configurations',
    component: ConfigurationsScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/data',
    component: TableDataScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/data/table/$tableId',
    component: TableDataDetailScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/search',
    component: SearchScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/search/table/$tableId',
    component: SearchDetailScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/reports',
    component: ReportsScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/reports/$type/create',
    component: CreateReportScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/strategic-view/$queryType/$queryValue',
    component: StrategicViewScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/company',
    component: CompanyScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/summaries',
    component: SummariesScreen,
  }),
  createRoute({
    getParentRoute: () => authRoute,
    path: '/team',
    component: MyTeamScreen,
  }),
];

const routeTree = rootRoute.addChildren([authRoute.addChildren(protectedRoutes), ...publicRoutes]);

export const router = createRouter({ routeTree, defaultErrorComponent: RouterErrorFallback });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
